import React, { useEffect, useState } from 'react'
import { Alert, Image, Spin } from 'antd'

interface ChatImagePreviewProps {
  imageUrl: string
  alt?: string
  width?: number
  height?: number
}

const shouldLoadImageAsBlob = (imageUrl: string): boolean => {
  try {
    const { hostname } = new URL(imageUrl, window.location.origin)

    return hostname === 'storage.yandexcloud.net' || hostname.endsWith('.storage.yandexcloud.net')
  } catch (_error) {
    return false
  }
}

const ChatImagePreview: React.FC<ChatImagePreviewProps> = ({
  imageUrl,
  alt = 'Preview',
  width = 180,
  height = 140,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let isMounted = true

    const fetchImage = async () => {
      setLoading(true)
      setError(null)

      if (!shouldLoadImageAsBlob(imageUrl)) {
        if (isMounted) {
          setPreviewUrl(imageUrl)
          setLoading(false)
        }
        return
      }

      try {
        const response = await fetch(imageUrl, {
          method: 'GET',
          headers: {},
        })

        if (!response.ok) {
          throw new Error('Не удалось загрузить изображение')
        }

        const blob = await response.blob()
        objectUrl = URL.createObjectURL(blob)

        if (isMounted) {
          setPreviewUrl(objectUrl)
        }
      } catch (err) {
        if (isMounted) {
          setError('Ошибка загрузки изображения')
        }
        console.error(err)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchImage()

    return () => {
      isMounted = false

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [imageUrl])

  if (loading) {
    return <Spin size="small" />
  }

  if (error || !previewUrl) {
    return <Alert message={error || 'Не удалось отобразить изображение'} type="error" showIcon />
  }

  return (
    <Image
      src={previewUrl}
      alt={alt}
      width={width}
      height={height}
      preview
      placeholder={<Spin size="small" />}
      style={{ height, objectFit: 'cover', borderRadius: 10 }}
      onError={() => setError('Не удалось отобразить изображение')}
    />
  )
}

export default ChatImagePreview
